import { Injectable } from '@angular/core';
import { HttpService } from '../app/utility/http-service';

export interface ClientSyncConfig {
  id: string;
  name: string;
  clientId: string;
  dataSetId: string;
  enabled: boolean;
  jobKey: string;
  jobPayload: string;
}

export interface BulkSyncPayload {
  AllFields: boolean;
  LegacySync: boolean;
  NumberThreads: number;
  TablePrefix: null;
  ModDateField: string;
  ClientConnectionId: string;
  Tables: BulkSyncTable[];
}

export interface BulkSyncTable {
  Schema: string;
  TableName: string;
  GroupByColumns: string[];
  Columns: BulkSyncColumn[];
}

export interface BulkSyncColumn {
  AggregationType: string;
  ColumnName: string;
  ColumnMapping: string | null;
}

export interface DataSetOption {
  id: string;
  name: string;
}

export interface DataModelTable {
  id: string;
  schema: string | null;
  tableName: string;
  displayName: string;
  fromMajorVersion: number;
  fromMinorVersion: number;
  columns: string[];
}

export interface FieldModelDto {
  id: string;
  tableId: string;
  fieldName: string;
  displayName: string;
  fieldTypeString: string;
  isNullable: boolean;
  isPrimaryKey: boolean;
  isUniqueKeyPart: boolean;
  keyOrder: number | null;
  charLimit: number | null;
  isLargeText: boolean | null;
  fieldFormat: string | null;
  fromMajorVersion: number;
  fromMinorVersion: number;
}

export interface SyncConnection {
  id: string;
  name: string;
}

export const AGGREGATION_TYPES = ['None', 'Sum', 'Min', 'Max', 'Count', 'Average'] as const;

@Injectable({ providedIn: 'root' })
export class ClientSyncService {
  private readonly base = 'ClientSync';

  constructor(private http: HttpService) {}

  getConfigs(clientId: string, onSuccess: (configs: ClientSyncConfig[]) => void): void {
    this.http.get<ClientSyncConfig[]>(`${this.base}/GetConfigs?clientId=${clientId}`, onSuccess);
  }

  saveConfig(config: ClientSyncConfig, onSuccess: () => void, onError?: () => void): void {
    this.http.postReturn(`${this.base}/SaveConfig`, onSuccess, undefined, config, onError);
  }

  deleteConfig(configId: string, onSuccess: () => void, onError?: () => void): void {
    this.http.deleteReturn(`${this.base}/DeleteConfig?configId=${configId}`, onSuccess, onError);
  }

  // Returns a flat list of this client's data sets (id + name) for the dropdown.
  getDataSets(clientId: string, onSuccess: (sets: DataSetOption[]) => void): void {
    this.http.get<DataSetOption[]>(`${this.base}/GetDataSets?clientId=${clientId}`, onSuccess);
  }

  // Reuses the existing feed-connections endpoint filtered to Data connections.
  getConnections(clientId: string, onSuccess: (conns: SyncConnection[]) => void): void {
    this.http.get<SyncConnection[]>(
      `ClientSettings/GetFeedConnections?clientId=${clientId}&connectionType=Data`,
      onSuccess
    );
  }

  // Returns the data model (tables + columns) for a given data set.
  getDataModel(dataSetId: string, onSuccess: (tables: DataModelTable[]) => void): void {
    this.http.get<DataModelTable[]>(
      `${this.base}/GetDataModel?dataSetId=${dataSetId}`,
      onSuccess
    );
  }

  getFieldsByTableId(tableId: string, onSuccess: (fields: FieldModelDto[]) => void): void {
    this.http.get<FieldModelDto[]>(
      `${this.base}/GetFieldsByTableId?tableId=${tableId}`,
      onSuccess
    );
  }

  updateTableModel(
    id: string,
    displayName: string,
    fromMajorVersion: number,
    fromMinorVersion: number,
    onSuccess: () => void,
    onError?: () => void
  ): void {
    this.http.postReturn(
      `${this.base}/UpdateTableModel`,
      onSuccess,
      undefined,
      { id, displayName, fromMajorVersion, fromMinorVersion },
      onError
    );
  }
}
